import { useContext, useEffect, useState } from "react";
import {
  Alert, Button, Col, DatePicker, Form, Input, Modal, Popconfirm,
  Row, Select, Space, Table, Tag, Typography, message
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ConfiguratorContext } from "../context";
import { CreatePersonalAccessTokenRequest, PatTokenMode, PersonalAccessToken } from "../api/models";

const { Title, Paragraph, Text } = Typography;

const MODE_LABELS: Record<PatTokenMode, string> = {
  FULL: "Full access",
  READ_ONLY: "Read-only",
  CUSTOM: "Custom",
};

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  expired: "orange",
  revoked: "red",
};

const PersonalAccessTokensPage = () => {
  const context = useContext(ConfiguratorContext);
  const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<string | undefined>();
  const [mode, setMode] = useState<PatTokenMode>("READ_ONLY");
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadTokens();
    loadPermissions();
  }, []);

  const loadTokens = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const resp = await context.api.fetchPersonalAccessTokens();
      setTokens(resp.data);
    } catch (e) {
      console.error("Failed to load access tokens", e);
      setError("Unable to load access tokens. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const resp = await context.api.fetchPersonalAccessTokenPermissions();
      setPermissions(resp.data);
    } catch (e) {
      console.error("Failed to load available permissions", e);
    }
  };

  const handleCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const request: CreatePersonalAccessTokenRequest = {
        name: values.name,
        mode: values.mode,
        expiresAt: (values.expiresAt as Dayjs).toISOString(),
        scopedPermissions: values.mode === "CUSTOM" ? values.scopedPermissions : undefined,
      };
      const resp = await context.api.createPersonalAccessToken(request);
      setSecret(resp.data.secret);
      setCreateOpen(false);
      form.resetFields();
      setMode("READ_ONLY");
      loadTokens();
    } catch (e) {
      console.error("Failed to create access token", e);
      message.error("Unable to create the access token. Please check the details and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (token: PersonalAccessToken) => {
    try {
      await context.api.revokePersonalAccessToken(token.id);
      message.success(`Revoked "${token.name}"`);
      loadTokens();
    } catch (e) {
      console.error("Failed to revoke access token", e);
      message.error("Unable to revoke the access token. Please try again later.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, row: PersonalAccessToken) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: ".8rem" }}>…{row.lastFour}</Text>
        </Space>
      ),
    },
    { title: "Owner", dataIndex: "ownerEmail", key: "ownerEmail" },
    {
      title: "Access",
      dataIndex: "mode",
      key: "mode",
      render: (m: PatTokenMode, row: PersonalAccessToken) => (
        <Space direction="vertical" size={0}>
          <Tag color={m === "FULL" ? "volcano" : m === "READ_ONLY" ? "blue" : "purple"}>
            {MODE_LABELS[m]}
          </Tag>
          {m === "CUSTOM" && (
            <Text type="secondary" style={{ fontSize: ".75rem" }}>
              {(row.scopedPermissions ?? []).length} permission(s)
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => <Tag color={STATUS_COLORS[s] ?? "default"}>{s}</Tag>,
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (d?: string) => (d ? dayjs(d).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (d?: string) => (d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "Never"),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, row: PersonalAccessToken) =>
        row.status === "active" ? (
          <Popconfirm
            title="Revoke this token?"
            description="Any integration using it stops working immediately."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleRevoke(row)}
          >
            <Button danger size="small">Revoke</Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: "1rem 2rem" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>Personal Access Tokens</Title>
          <Paragraph type="secondary">
            Tokens authenticate API calls as you. They can never do more than your own
            permissions allow, and lose access automatically if your roles change.
          </Paragraph>
        </Col>
        <Col>
          <Button type="primary" onClick={() => setCreateOpen(true)}>Create token</Button>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: "1rem" }} />}

      <Table
        rowKey="id"
        dataSource={tokens}
        columns={columns}
        loading={loading}
        locale={{ emptyText: "No access tokens yet" }}
        pagination={false}
      />

      <Modal
        title="Create access token"
        open={createOpen}
        onOk={handleCreate}
        confirmLoading={saving}
        okText="Create"
        onCancel={() => { setCreateOpen(false); form.resetFields(); setMode("READ_ONLY"); }}
      >
        <Form form={form} layout="vertical" initialValues={{ mode: "READ_ONLY" }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Give the token a recognisable name" }]}
          >
            <Input placeholder="e.g. mcp-gateway" />
          </Form.Item>
          <Form.Item
            name="expiresAt"
            label="Expires"
            rules={[{ required: true, message: "An expiry date is required" }]}
            extra="Required. Maximum one year."
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(d) => d && (d.isBefore(dayjs()) || d.isAfter(dayjs().add(365, "day")))}
            />
          </Form.Item>
          <Form.Item name="mode" label="Access">
            <Select onChange={(v: PatTokenMode) => setMode(v)}>
              <Select.Option value="READ_ONLY">Read-only — cannot change anything</Select.Option>
              <Select.Option value="FULL">Full — everything you can do</Select.Option>
              <Select.Option value="CUSTOM">Custom — pick specific permissions</Select.Option>
            </Select>
          </Form.Item>
          {mode === "CUSTOM" && (
            <Form.Item
              name="scopedPermissions"
              label="Permissions"
              rules={[{ required: true, message: "Select at least one permission" }]}
              extra="Only permissions you currently hold are listed."
            >
              <Select mode="multiple" allowClear placeholder="Select permissions"
                      options={permissions.map(p => ({ label: p, value: p }))} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Copy your access token"
        open={!!secret}
        onCancel={() => setSecret(undefined)}
        footer={[<Button key="done" type="primary" onClick={() => setSecret(undefined)}>Done</Button>]}
      >
        <Alert
          type="warning"
          showIcon
          message="This is the only time the token will be shown."
          description="Store it somewhere safe now. If you lose it, revoke the token and create a new one."
          style={{ marginBottom: "1rem" }}
        />
        <Input.TextArea readOnly value={secret} autoSize style={{ fontFamily: "monospace" }} />
        <Button
          style={{ marginTop: ".75rem" }}
          onClick={() => { navigator.clipboard?.writeText(secret ?? ""); message.success("Copied"); }}
        >
          Copy to clipboard
        </Button>
      </Modal>
    </div>
  );
};

export default PersonalAccessTokensPage;
